/**
 * Shopping Preferences Types
 *
 * Centralized type definitions for the shopping preferences feature.
 * These types are used across storage, sync, and UI layers.
 */

export const WOMEN_UNIVERSE = 1;
export const MEN_UNIVERSE = 2;

export type Universe = typeof WOMEN_UNIVERSE | typeof MEN_UNIVERSE;

/**
 * Shopping preferences payload structure
 * Used for API requests/responses and storage
 */
export interface ShoppingPrefsPayload {
  universe?: number;
  categories?: number[];
  custom?: string[];
  updated_at?: string;
}

/**
 * Storage type for preferences
 */
export type StorageType = 'permanent' | 'temporary';

/**
 * Complete shopping preferences with metadata
 */
export interface ShoppingPreferences extends ShoppingPrefsPayload {
  last_updated: string;
  storage_type?: StorageType;
}

/**
 * Preferences without timestamp (used for updates)
 */
export type PreferencesWithoutTimestamp = Omit<ShoppingPreferences, 'last_updated'>;

/**
 * Source of current preferences
 */
export type StorageSource = 'cookie' | 'backend' | 'default' | null;

/**
 * Storage to clear
 */
export type StorageToClear = 'temporary' | 'permanent' | 'all';

/**
 * Cookie preferences with storage type
 */
export interface CookiePrefs extends ShoppingPrefsPayload {
  storage_type: StorageType;
  source?: StorageSource;
}

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors?: string[];
}

/**
 * Sync result
 */
export interface SyncResult {
  success: boolean;
  source: StorageSource;
  preferences: ShoppingPreferences;
  sessionPrefs: ShoppingPreferences | null;
  error?: Error;
}

/**
 * Hook return type
 */
export interface UseShoppingPreferencesReturn {
  preferences: ShoppingPreferences | null;
  sessionPrefs: ShoppingPreferences | null;
  updatePreferences: (newPrefs: PreferencesWithoutTimestamp) => Promise<void>;
  setSessionPreferences: (newPrefs: PreferencesWithoutTimestamp) => void;
  clearPreferences: (storage: StorageToClear) => void;
  source: StorageSource;
  isLoading: boolean;
  isError: boolean;
  isEnabled: boolean;
  hasUserSelectedPrefs: boolean;
}

/**
 * Context value type
 */
export interface ShoppingPreferencesContextValue {
  preferences: ShoppingPreferences | null;
  sessionPrefs: ShoppingPreferences | null;
  source: StorageSource;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isEnabled: boolean;
  hasUserSelectedPrefs: boolean;
  updatePreferences: (newPrefs: PreferencesWithoutTimestamp) => Promise<void>;
  setSessionPreferences: (newPrefs: PreferencesWithoutTimestamp) => void;
  clearPreferences: (storage: StorageToClear) => void;
  refetch: () => Promise<void>;
}

/**
 * Modal state type
 */
export interface ShoppingPreferencesModalState {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}
